import cloneDeep from 'lodash/cloneDeep';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { traverse } from '@atlaskit/adf-utils/traverse/main';
import type { ADNode } from '@atlaskit/editor-common/utils/validator';

import { expVal } from '@confluence/feature-experiments';
import { getLogger } from '@confluence/logger';
import { getHeadingId } from '@confluence/editor-heading-links/entry-points/headingLinksFromElements.ts';

const logger = getLogger('table-of-contents');

/**
 * Module-level WeakMap cache so multiple TOC macros on the same page share
 * a single ADF traversal.
 */
const headingsCache = new WeakMap<object, Map<string, ADFEntity[]>>();

const buildCacheKey = (
	minConfig: number,
	maxConfig: number,
	includeRegex: string | undefined,
	excludeRegex: string | undefined,
) => `${minConfig}|${maxConfig}|${includeRegex ?? ''}|${excludeRegex ?? ''}`;
/**
 *
 * @param macroNode
 * @param adf - optional
 *
 * Used to handle options provided by adf to create the TOC
 */
export const handleTocNode = (
	macroNode: ADNode,
	adf: ADNode | string | null,
): {
	className: string;
	cssliststyle: any;
	csslistindent: any;
	separators: any;
	headerelements: ADFEntity[];
	headerRange: string[];
	hasbody: string;
	name: any;
	structure: any;
	outline: any;
	printable: any;
	macroId: any;
} => {
	const minConfig = Math.max(
		1,
		parseInt(macroNode?.attrs?.parameters?.macroParams?.minLevel?.value) || 1,
	);
	const maxConfig = Math.min(
		6,
		parseInt(macroNode?.attrs?.parameters?.macroParams?.maxLevel?.value) || 6,
	);
	const macroParams = macroNode?.attrs?.parameters?.macroParams;

	const includeheaderregex = macroParams?.include?.value;
	const excludeheaderregex = macroParams?.exclude?.value;

	// Helper function to go through heading content
	// and test regular expression against texts from content
	function filterHeadingWithRegex(node: ADFEntity) {
		if (!node?.content) {
			return false;
		}
		if (!includeheaderregex && !excludeheaderregex) {
			return true;
		}
		try {
			let includeRegex;
			if (includeheaderregex) {
				includeRegex = new RegExp(includeheaderregex);
			}
			let excludeRegex;
			if (excludeheaderregex) {
				excludeRegex = new RegExp(excludeheaderregex);
			}
			let fullHeaderString = '';
			for (const el of node?.content) {
				fullHeaderString +=
					el?.text || el?.attrs?.text || el?.attrs?.__confluenceMetadata?.contentTitle || '';
			}
			if (includeRegex && !includeRegex.test(fullHeaderString)) {
				return false;
			}
			if (excludeRegex && excludeRegex.test(fullHeaderString)) {
				return false;
			}
		} catch (error) {
			logger.error`@@@@@ Failed to handle regular expression - ${error}`;
		}

		return true;
	}
	// Hold a list of headers to look for duplicates
	// duplicated headers require an additional index/key which appends the link
	const headingsIds = new Set<string>();

	// NOTE: ADFNode and ADFEntity share the same shape but ADFEntity allows for more keys
	let adfObject = adf as ADFEntity;
	if (typeof adf === 'string') {
		try {
			adfObject = JSON.parse(adf);
		} catch {
			// Swallow the error as ADF format error handling is expected to be done earlier,
			// in the component that renders the entire content
		}
	}

	const adfWasString = typeof adf === 'string';
	const headerelements = buildHeadings(expVal('confluence_toc_render_perf', 'isEnabled', false), {
		adfObject,
		adfWasString,
		minConfig,
		maxConfig,
		includeheaderregex,
		excludeheaderregex,
		headingsIds,
		filterHeadingWithRegex,
	});

	return {
		className: `${macroParams?.class?.value || 'macro-core'} toc-macro conf-macro output-block`,
		cssliststyle: macroParams?.style?.value,
		csslistindent: macroParams?.indent?.value,
		separators: macroParams?.separator?.value,
		headerelements,
		headerRange: ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].slice(minConfig - 1, maxConfig),
		hasbody: macroNode?.attrs.bodyType === 'none' ? 'false' : 'true',
		name: macroNode?.attrs?.extensionKey,
		structure: macroParams?.type?.value,
		outline: macroParams?.outline?.value,
		printable: macroParams?.printable?.value || 'true',
		macroId: macroNode?.attrs?.parameters?.macroMetadata?.macroId?.value,
	};
};

// Bundled traversal context for `buildHeadings`. Grouped into a single param to
// keep the function under the project's max-arity lint rule (7).
interface BuildHeadingsContext {
	adfObject: ADFEntity;
	adfWasString: boolean;
	minConfig: number;
	maxConfig: number;
	includeheaderregex: string | undefined;
	excludeheaderregex: string | undefined;
	headingsIds: Set<string>;
	filterHeadingWithRegex: (node: ADFEntity) => boolean;
}

const buildHeadings = (
	usePerfPath: boolean,
	{
		adfObject,
		adfWasString,
		minConfig,
		maxConfig,
		includeheaderregex,
		excludeheaderregex,
		headingsIds,
		filterHeadingWithRegex,
	}: BuildHeadingsContext,
): ADFEntity[] => {
	const headingElements: ADFEntity[] = [];
	let cachedHeadings: ADFEntity[] | undefined;
	let cacheBucket: Map<string, ADFEntity[]> | undefined;
	let cacheKey: string | undefined;
	if (adfObject && typeof adfObject === 'object' && !adfWasString && usePerfPath) {
		cacheKey = buildCacheKey(minConfig, maxConfig, includeheaderregex, excludeheaderregex);
		cacheBucket = headingsCache.get(adfObject);
		if (!cacheBucket) {
			cacheBucket = new Map();
			headingsCache.set(adfObject, cacheBucket);
		}
		cachedHeadings = cacheBucket.get(cacheKey);
	}

	if (cachedHeadings) {
		// Push cached entries; downstream code mutates the array but not its members.
		headingElements.push(...cachedHeadings);
		return headingElements;
	}

	// Create a reusable visitor collection that can handle both headings and extensions (LCMs)
	const createVisitors = (): any => ({
		heading: (node) => {
			if (filterHeadingWithRegex(node)) {
				let clone: ADFEntity;
				if (usePerfPath) {
					// Shallow clone is sufficient: we only assign attrs.id, and downstream consumers
					// (MiniRenderer / renderContent) only READ node.content. This avoids the
					// expensive deep clone of the whole heading subtree
					clone = {
						...node,
						attrs: { ...node.attrs, id: getHeadingId(node, headingsIds) },
					};
				} else {
					// init deep-copy node
					clone = cloneDeep(node);
					clone.attrs = clone.attrs || {};
					clone.attrs.id = getHeadingId(clone, headingsIds);
				}
				if (node?.attrs?.level >= minConfig && node?.attrs?.level <= maxConfig) {
					headingElements.push(clone);
				}
			}
		},
		// Traverse into extension nodes (Legacy Content Macros) to find nested headings
		extension: (node) => {
			const nestedContent = node?.attrs?.parameters?.nestedContent;
			if (nestedContent && typeof nestedContent === 'object') {
				// Recursively traverse the nested content with the same visitors
				// This allows us to handle nested LCMs (LCMs within LCMs)
				traverse(nestedContent as ADFEntity, createVisitors());
			}
			return node;
		},
	});

	// Traverse through ADF and filter out nodes that doesn't belong in the TOC
	// Options includes (minLevel, maxLevel, includeheaderregex, excludeheaderrregex)
	traverse((adfObject || {}) as ADFEntity, createVisitors());

	if (cacheBucket && cacheKey) {
		// Store a copy so subsequent mutations of the returned array don't pollute the cache.
		cacheBucket.set(cacheKey, headingElements.slice());
	}

	return headingElements;
};
